/**
 * Copyright (c) 2025 Aisle 24. All rights reserved.
 *
 * Proprietary Software - Licensed for Internal Use Only
 *
 * This software and associated documentation files (the "Software") are proprietary
 * to Aisle 24 and are protected by copyright laws and international copyright
 * treaties, as well as other intellectual property laws and treaties.
 *
 * The Software is licensed, not sold, to you for use only under the terms of this
 * license agreement. All rights not expressly granted to you are reserved by Aisle 24.
 *
 * Unauthorized copying, modification, distribution, or use of this Software,
 * via any medium is strictly prohibited and may result in severe civil and
 * criminal penalties, and will be prosecuted to the maximum extent possible
 * under the law.
 */

import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, AppBar, Toolbar, Typography, IconButton } from '@mui/material';
import { ShoppingCart } from '@mui/icons-material';
import {
  CartInterface,
  PaymentInterface,
  ReceiptInterface
} from './components';
import ScannerInterface from './components/ScannerInterface';
import { UpdateNotification } from './components/UpdateNotification';
import { CartItem } from './types/CartItem';
import { createOntarioTechTheme } from './theme/ontarioTechTheme';
import { UI_TEXT, KIOSK_CONFIG, APP_CONFIG } from './constants/appConstants';
import { CURRENT_BRANDING } from './config/brandingConfig';
import { updateChecker, UpdateCheckResult } from './services/UpdateChecker';
import type { ReceiptPayload } from './types/ReceiptTypes';
import AuthInterface from './components/AuthInterface';

// Create the Ontario Tech theme (default development theme)
const theme = createOntarioTechTheme();

// ✅ ADD auth state
type AppState = 'auth' | 'scanning' | 'cart' | 'payment' | 'receipt';

function App() {
  const [currentState, setCurrentState] = useState<AppState>(() => {
    const authed = localStorage.getItem('pos_authed') === 'true';
    return authed ? 'scanning' : 'auth';
  });

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isKioskMode, setIsKioskMode] = useState(false);
  const [updateInfo, setUpdateInfo] = useState<UpdateCheckResult | null>(null);

  // ✅ holds final priced receipt (with promos/tax/deposit)
  const [receipt, setReceipt] = useState<ReceiptPayload | null>(null);

  // ✅ helper
  const isAuthed = () => localStorage.getItem('pos_authed') === 'true';
  const handleLogout = () => {
    localStorage.removeItem("pos_authed");
    localStorage.removeItem("pos_user_email");
    localStorage.removeItem("pos_last_auth_date");

    setCartItems([]);
    setReceipt(null);
    setCurrentState("auth");
  };


  // ✅ safe navigation: if not authed, force auth screen
  const goTo = (next: AppState) => {
    if (!isAuthed() && next !== 'auth') {
      setCurrentState('auth');
      return;
    }
    setCurrentState(next);
  };

  // Enable kiosk mode on load
  useEffect(() => {
    const enableKioskMode = () => {
      setIsKioskMode(true);

      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch((err) => {
          console.log('Fullscreen not available:', err);
        });
      }
    };

    // Enable kiosk mode after a short delay
    const timer = setTimeout(enableKioskMode, KIOSK_CONFIG.FULLSCREEN_DELAY);
    return () => clearTimeout(timer);
  }, []);

  // ✅ If someone clears localStorage manually while app is open
  useEffect(() => {
    if (!isAuthed() && currentState !== 'auth') {
      setCurrentState('auth');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentState]);

  // Calculate base total amount whenever cart changes (still used for cart display)
  useEffect(() => {
    const total = cartItems.reduce((sum: number, item: CartItem) => sum + (item.price * item.quantity), 0);
    setTotalAmount(total);
  }, [cartItems]);

  // Check for updates on mount and periodically
  useEffect(() => {
    updateChecker.startAutoCheck((result) => {
      if (result.hasUpdate) setUpdateInfo(result);
    });

    return () => {
      updateChecker.stopAutoCheck();
    };
  }, []);

  const addToCart = (item: CartItem) => {
    setCartItems((prev: CartItem[]) => {
      const existingItem = prev.find((cartItem: CartItem) => cartItem.id === item.id);
      if (existingItem) {
        return prev.map((cartItem: CartItem) =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      }
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const updateCartItem = (id: string, quantity: number) => {
    if (quantity <= 0) {
      setCartItems((prev: CartItem[]) => prev.filter((item: CartItem) => item.id !== id));
    } else {
      setCartItems((prev: CartItem[]) =>
        prev.map((item: CartItem) =>
          item.id === id ? { ...item, quantity } : item
        )
      );
    }
  };

  const clearCart = () => {
    setCartItems([]);
    setReceipt(null); // ✅ reset receipt data
    goTo('scanning');
  };

  // ✅ Payment now returns receipt payload
  const handlePaymentComplete = (r: ReceiptPayload) => {
    setReceipt(r);
    goTo('receipt');
  };

  const renderCurrentInterface = () => {
    switch (currentState) {
      case 'auth':
        return (
          <AuthInterface
            onAuthenticated={() => {
              // Auth screen will set localStorage pos_authed=true
              goTo('scanning');
            }}
          />
        );

      case 'scanning':
        return (
          <ScannerInterface
            onItemScanned={addToCart}
            onViewCart={() => goTo('cart')}
          />
        );

      case 'cart':
        return (
          <CartInterface
            cartItems={cartItems}
            totalAmount={totalAmount}
            onUpdateItem={updateCartItem}
            onProceedToPayment={() => goTo('payment')}
            onBackToScanning={() => goTo('scanning')}
          />
        );

      case 'payment':
        return (
          <PaymentInterface
            totalAmount={totalAmount}
            onPaymentComplete={handlePaymentComplete}
            onBackToCart={() => goTo('cart')}
            cartItems={cartItems}
          />
        );

      case 'receipt':
        // Safety fallback
        if (!receipt) {
          return (
            <CartInterface
              cartItems={cartItems}
              totalAmount={totalAmount}
              onUpdateItem={updateCartItem}
              onProceedToPayment={() => goTo('payment')}
              onBackToScanning={() => goTo('scanning')}
            />
          );
        }

        return (
          <ReceiptInterface
            cartItems={cartItems}
            receipt={receipt}
            onNewTransaction={clearCart}
          />
        );

      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {updateInfo?.hasUpdate && (
        <UpdateNotification
          updateInfo={updateInfo}
          onDismiss={() => setUpdateInfo(null)}
          onUpdate={() => {
            // Update will be triggered by UpdateNotification component
          }}
        />
      )}
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'visible',
          backgroundColor: isKioskMode ? '#000' : '#f5f5f5',
        }}
      >
        {/* Header */}
        <AppBar position="static">
          <Toolbar sx={{ minHeight: '64px', px: 3 }}>
            {/* Tenant Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
              <Box
                component="img"
                src={CURRENT_BRANDING.images.logo}
                alt={`${CURRENT_BRANDING.name} Logo`}
                sx={{ height: 50, width: 'auto' }}
              />
            </Box>

            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '1.4rem',
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                }}
              >
                {UI_TEXT.HEADER.TITLE}
              </Typography>
              <Typography
                sx={{
                  color: '#E9ECEF',
                  fontSize: '1rem',
                  fontWeight: 500,
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                }}
              >
                {UI_TEXT.HEADER.SUBTITLE}
              </Typography>
              <Typography
                sx={{
                  color: '#B8C5D1',
                  fontSize: '0.8rem',
                  fontWeight: 400,
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  mt: 0.5,
                }}
              >
                v{APP_CONFIG.VERSION} (Build {APP_CONFIG.BUILD_NUMBER}) | POS {APP_CONFIG.POS_NUMBER}
              </Typography>
            </Box>

            {/* ✅ Only show cart icon when authed + scanning */}
            {isAuthed() && currentState === 'scanning' && (
              <IconButton
                color="inherit"
                onClick={() => goTo('cart')}
                sx={{
                  ml: 2,
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  border: '2px solid rgba(255,255,255,0.3)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.25)',
                    border: '2px solid rgba(255,255,255,0.5)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <ShoppingCart />
                {cartItems.length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      backgroundColor: '#F06C38',
                      color: '#FFFFFF',
                      borderRadius: '50%',
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      border: '2px solid #FFFFFF',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    }}
                  >
                    {cartItems.reduce((sum: number, item: CartItem) => sum + item.quantity, 0)}
                  </Box>
                )}
              </IconButton>
            )}
            {isAuthed() && (
              <Typography
                onClick={handleLogout}
                sx={{
                  ml: 3,
                  cursor: "pointer",
                  fontWeight: 600,
                  color: "#FFFFFF",
                  border: "1px solid rgba(255,255,255,0.4)",
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.15)"
                  }
                }}
              >
                Logout
              </Typography>
            )}

          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible' }}>
          {renderCurrentInterface()}
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
